/*
 * Tracking for a small set of "critical" interactions the data team needs with specific
 * properties (page type, section, element, value). These are sent straight to Amplitude as
 * `Critical Element Selected`, and as a `critical_element_selected` signal that is forwarded to
 * Segment.
 *
 * We prefer User Signals for durable events. Only add here when the data team needs properties a
 * signal can't provide, and keep the event and property lists short: every addition is another
 * Amplitude event/property to maintain.
 */

import { usePathname } from 'next/navigation'
import { useCallback } from 'react'

import { useAmplitude } from '../_components/chrome/scripts/AmplitudeScript'
import {
  resolveSecondaryLocation,
  useTrackingLocation,
} from '../_providers/TrackingLocationProvider.client'
import { useTrackCriticalElementSelected } from '../_providers/TrackingProvider.client'

// Again, obviously this could change. If we don't just add these properties everywhere though, the places where these "critical" events are firing from
// should be relatively static.
export type PageType = 'KIT' | 'COLLECTION' | 'PDP' | 'PAGE-VIEW'

export const usePageTypeTracking = (): PageType | null => {
  const pathname = usePathname()
  if (pathname?.includes('/products/')) return 'PDP'
  if (pathname?.includes('/kits/')) return 'KIT'
  if (pathname?.includes('/collections/')) return 'COLLECTION'
  // The root route renders the home page view.
  if (pathname === '/' || pathname?.includes('/pages/')) return 'PAGE-VIEW'
  return null
}

/**
 * Event names agreed with the data team for static tracking. Keep this list short.
 */
export type StaticAmplitudeEventName = 'Critical Element Selected'

/**
 * Subsection of the page where the interaction occurred.
 */
export type StaticTrackingSecondaryLocation =
  | 'Main Product'
  | 'Core'
  | 'Embroidery Modal'
  | 'Complete the Set'
  | 'Wear It With'
  | 'Picks for You'
  | 'Recently Viewed'
  | 'Best Sellers'
  | 'Shop Core Colors'
  | 'Buy It Again'
  | 'Favorites'
  | 'Kit'
  | 'Claim Gift'
  | 'Bulk Order'
  | 'Product Details Dialog'

/**
 * The element the user interacted with.
 */
type StaticTrackingModifiedElement =
  | 'Size Selector'
  | 'Fit Selector'
  | 'Color Swatch'
  | 'Quantity Selector'
  | 'Size Chart'
  | 'Add Embroidery'
  | 'Module Tile'
  | 'Module Link'

type StaticAmplitudeEventProperties = {
  productHandle?: string
  secondaryTrackingLocation?: StaticTrackingSecondaryLocation
  modifiedElement: StaticTrackingModifiedElement
  // never a translated display value.
  modifiedValue?: string
  href?: string
}

/**
 * Sends a static tracking event to Amplitude. `location` is always the current page type, and
 * `section_number` / `position` come from the nearest `TrackingLocationProvider`.
 */
export const useStaticAmplitudeTracking = () => {
  const { track } = useAmplitude()
  const { trackCriticalElementSelected } = useTrackCriticalElementSelected()
  const pageType = usePageTypeTracking()
  const trackingLocation = useTrackingLocation()

  const trackStaticAmplitudeEvent = useCallback(
    (properties: StaticAmplitudeEventProperties) => {
      const eventProperties = {
        // `items` like the cart signals, so funnels can hold `items.handle` constant.
        items: properties.productHandle ? [{ handle: properties.productHandle }] : undefined,
        location: pageType,
        secondary_location:
          properties.secondaryTrackingLocation ?? resolveSecondaryLocation(trackingLocation),
        section_number: trackingLocation.sectionNumber,
        position: trackingLocation.position,
        modified_element: properties.modifiedElement,
        modified_value: properties.modifiedValue,
        href: properties.href,
      }
      track({
        eventName: 'Critical Element Selected',
        eventId: crypto.randomUUID(),
        eventProperties,
      })
      trackCriticalElementSelected(eventProperties)
    },
    [track, trackCriticalElementSelected, pageType, trackingLocation]
  )

  return { trackStaticAmplitudeEvent }
}
